import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Navigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import DOMPurify from 'dompurify';
import { 
  Save, 
  Eye, 
  ArrowLeft, 
  Loader2, 
  CheckCircle,
  AlertCircle,
  Image as ImageIcon,
  X,
  Tag,
  Plus,
  Volume2
} from 'lucide-react';
import { useAuth } from '../lib/AuthContext';
import { fetchBlogById, createBlogPost, updateBlogPost } from '../lib/firebaseBlog';

const CATEGORIES = [
  { value: 'tech', label: 'Syntax & Systems' },
  { value: 'rabbit_hole', label: 'Rabbit Hole' },
];

const quillModules = {
  toolbar: [
    [{ 'header': [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    ['blockquote', 'code-block'],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    [{ 'indent': '-1'}, { 'indent': '+1' }],
    ['link', 'image'],
    ['clean']
  ],
};

const quillFormats = [
  'header',
  'bold', 'italic', 'underline', 'strike',
  'blockquote', 'code-block',
  'list', 'bullet', 'indent',
  'link', 'image'
];

// Helper to strip HTML for TTS
function stripHtml(html) {
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent || div.innerText || '';
}

export function AdminPostEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const isEditing = id && id !== 'new';

  const [formData, setFormData] = useState({
    title: '',
    category: 'tech',
    excerpt: '',
    content: '',
    tags: [],
    featuredImage: '',
    published: false,
    audioUrl: '',
  });

  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');
  const [newTag, setNewTag] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (isEditing && isAuthenticated) {
      loadPost();
    }
  }, [isEditing, isAuthenticated]);

  const loadPost = async () => {
    try {
      const post = await fetchBlogById(id, true);
      if (post) {
        setFormData({
          title: post.title || '',
          category: post.category || 'tech',
          excerpt: post.excerpt || '',
          content: post.content || '',
          tags: post.tags || [],
          featuredImage: post.featuredImage || '',
          published: post.published || false,
          audioUrl: post.audioUrl || '',
        });
      }
    } catch (err) {
      console.error('Error loading post:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setSaveStatus(null);
  };

  const handleAddTag = () => {
    const tag = newTag.trim();
    if (tag && !formData.tags.includes(tag)) {
      setFormData(prev => ({ ...prev, tags: [...prev.tags, tag] }));
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tagToRemove)
    }));
  };

  // Generate TTS audio via Vercel serverless function (stores in Vercel Blob)
  const generateAudio = async (postId, content) => {
    try {
      setStatusMessage('Generating audio narration...');
      
      const plainText = stripHtml(content);
      if (!plainText || plainText.length < 10) {
        console.log('Content too short for TTS');
        return null;
      }

      const response = await fetch('/api/generate-tts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: plainText, voiceName: 'Kore', postId }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to generate audio');
      }

      const data = await response.json();
      setStatusMessage('Audio ready!');
      return data.audioUrl;
    } catch (err) {
      console.error('Audio generation error:', err.message || err);
      setStatusMessage('Audio generation failed (post saved without audio)');
      return null;
    }
  };

  const handleSave = async (publish = false) => {
    setSaving(true);
    setSaveStatus(null);
    setStatusMessage('Saving post...');

    try {
      let postId = id;
      let audioUrl = formData.audioUrl;

      // If publishing, generate new audio
      if (publish) {
        // First save/create the post to get an ID
        const postData = {
          ...formData,
          published: false, // Will update to true after audio generation
        };

        if (isEditing) {
          await updateBlogPost(id, postData);
        } else {
          const newPost = await createBlogPost(postData);
          postId = newPost.id;
          navigate(`/admin/posts/${postId}`, { replace: true });
        }

        // Generate audio
        const newAudioUrl = await generateAudio(postId, formData.content);
        if (newAudioUrl) {
          audioUrl = newAudioUrl;
        }

        // Now update with published=true and audioUrl
        await updateBlogPost(postId, { 
          published: true, 
          audioUrl: audioUrl 
        });

        setFormData(prev => ({ ...prev, published: true, audioUrl }));
      } else {
        // Just save as draft (no audio generation)
        const postData = {
          ...formData,
          published: formData.published,
        };

        if (isEditing) {
          await updateBlogPost(id, postData);
        } else {
          const newPost = await createBlogPost(postData);
          navigate(`/admin/posts/${newPost.id}`, { replace: true });
        }
      }

      setSaveStatus('success');
      setStatusMessage(publish ? 'Published with audio!' : 'Draft saved');
      setTimeout(() => {
        setSaveStatus(null);
        setStatusMessage('');
      }, 3000);
    } catch (err) {
      console.error('Error saving post:', err);
      setSaveStatus('error');
      setStatusMessage('Error saving post');
    } finally {
      setSaving(false);
    }
  };

  // Regenerate audio for existing post
  const handleRegenerateAudio = async () => {
    if (!isEditing) return;
    
    setSaving(true);
    setStatusMessage('Regenerating audio...');
    
    try {
      const newAudioUrl = await generateAudio(id, formData.content);
      if (newAudioUrl) {
        await updateBlogPost(id, { audioUrl: newAudioUrl });
        setFormData(prev => ({ ...prev, audioUrl: newAudioUrl }));
        setSaveStatus('success');
        setStatusMessage('Audio regenerated!');
      }
    } catch (err) {
      console.error('Error regenerating audio:', err);
      setSaveStatus('error');
      setStatusMessage('Failed to regenerate audio');
    } finally {
      setSaving(false);
      setTimeout(() => {
        setSaveStatus(null);
        setStatusMessage('');
      }, 3000);
    }
  };

  const getPlainTextExcerpt = useCallback(() => {
    const div = document.createElement('div');
    div.innerHTML = formData.content;
    const text = div.textContent || div.innerText || '';
    return text.substring(0, 200) + (text.length > 200 ? '...' : '');
  }, [formData.content]);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/admin" replace />;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-card border-b border-border">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link
              to="/admin/dashboard"
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-body text-sm hidden sm:inline">Back to Dashboard</span>
            </Link>
          </div>

          <div className="flex items-center gap-3">
            {/* Status Messages */}
            {statusMessage && (
              <motion.span
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                className={`flex items-center gap-1 font-body text-sm ${
                  saveStatus === 'success' ? 'text-accent' : 
                  saveStatus === 'error' ? 'text-destructive' : 
                  'text-muted-foreground'
                }`}
              >
                {saveStatus === 'success' && <CheckCircle className="w-4 h-4" />}
                {saveStatus === 'error' && <AlertCircle className="w-4 h-4" />}
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {statusMessage}
              </motion.span>
            )}

            <button
              onClick={() => setShowPreview(true)}
              className="flex items-center gap-2 px-4 py-2 bg-muted text-foreground font-body text-sm rounded-lg hover:bg-muted/80 transition-colors"
              data-testid="preview-button"
            >
              <Eye className="w-4 h-4" />
              <span className="hidden sm:inline">Preview</span>
            </button>

            <button
              onClick={() => handleSave(false)}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-secondary text-secondary-foreground font-body text-sm rounded-lg hover:bg-secondary/90 transition-colors disabled:opacity-50"
              data-testid="save-draft-button"
            >
              <Save className="w-4 h-4" />
              <span className="hidden sm:inline">Save Draft</span>
            </button>

            <button
              onClick={() => handleSave(true)}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground font-body text-sm rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
              data-testid="publish-button"
            >
              {formData.published ? 'Republish' : 'Publish'}
            </button>
          </div>
        </div>
      </header>

      {/* Editor Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="space-y-6">
          {/* Title */}
          <div>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder="Post Title"
              className="w-full px-0 py-4 bg-transparent border-0 border-b-2 border-border font-heading text-3xl md:text-4xl font-bold text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary transition-colors"
              data-testid="post-title-input"
            />
          </div>

          {/* Category & Meta */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block font-body text-sm font-medium text-foreground mb-2">
                Category
              </label>
              <select
                value={formData.category}
                onChange={(e) => handleChange('category', e.target.value)}
                className="w-full px-4 py-3 bg-input border border-border rounded-lg font-body text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors"
                data-testid="category-select"
              >
                {CATEGORIES.map(cat => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block font-body text-sm font-medium text-foreground mb-2">
                Featured Image URL
              </label>
              <div className="relative">
                <ImageIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <input
                  type="url"
                  value={formData.featuredImage}
                  onChange={(e) => handleChange('featuredImage', e.target.value)}
                  placeholder="https://example.com/image.jpg"
                  className="w-full pl-10 pr-4 py-3 bg-input border border-border rounded-lg font-body text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors"
                  data-testid="featured-image-input"
                />
              </div>
            </div>
          </div>

          {/* Audio Status */}
          {isEditing && (
            <div className="p-4 bg-muted/50 border border-border rounded-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Volume2 className="w-5 h-5 text-primary" />
                  <div>
                    <p className="font-body font-medium text-foreground">
                      Audio Narration
                    </p>
                    <p className="font-body text-xs text-muted-foreground">
                      {formData.audioUrl 
                        ? 'Audio available - will regenerate on republish' 
                        : 'No audio yet - will generate on publish'}
                    </p>
                  </div>
                </div>
                {formData.audioUrl && (
                  <button
                    onClick={handleRegenerateAudio}
                    disabled={saving}
                    className="px-3 py-1.5 bg-primary/10 text-primary font-body text-sm rounded-lg hover:bg-primary/20 transition-colors disabled:opacity-50"
                  >
                    Regenerate Audio
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Excerpt */}
          <div>
            <label className="block font-body text-sm font-medium text-foreground mb-2">
              Excerpt
            </label>
            <textarea
              value={formData.excerpt}
              onChange={(e) => handleChange('excerpt', e.target.value)}
              placeholder="Brief summary of your post (used in blog cards and SEO)"
              rows={3}
              className="w-full px-4 py-3 bg-input border border-border rounded-lg font-body text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors resize-none"
              data-testid="excerpt-input"
            />
            <p className="mt-1 font-body text-xs text-muted-foreground">
              {formData.excerpt.length}/200 characters recommended
            </p>
          </div>

          {/* Tags */}
          <div>
            <label className="block font-body text-sm font-medium text-foreground mb-2">
              Tags
            </label>
            <div className="flex flex-wrap gap-2 mb-2">
              {formData.tags.map(tag => (
                <span
                  key={tag}
                  className="inline-flex items-center gap-1 px-3 py-1 bg-primary/10 text-primary font-body text-sm rounded-full"
                >
                  <Tag className="w-3 h-3" />
                  {tag}
                  <button
                    onClick={() => handleRemoveTag(tag)}
                    className="ml-1 hover:text-destructive transition-colors"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                placeholder="Add a tag"
                className="flex-1 px-4 py-2 bg-input border border-border rounded-lg font-body text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors"
                data-testid="tag-input"
              />
              <button
                onClick={handleAddTag}
                className="px-4 py-2 bg-muted text-foreground font-body text-sm rounded-lg hover:bg-muted/80 transition-colors"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Content Editor */}
          <div>
            <label className="block font-body text-sm font-medium text-foreground mb-2">
              Content
            </label>
            <div className="border border-border rounded-lg overflow-hidden bg-card">
              <ReactQuill
                theme="snow"
                value={formData.content}
                onChange={(value) => handleChange('content', value)}
                modules={quillModules}
                formats={quillFormats}
                placeholder="Write your blog post content here..."
                className="blog-editor"
                data-testid="content-editor"
              />
            </div>
          </div>
        </div>
      </main>

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-4xl max-h-[90vh] bg-background rounded-lg overflow-hidden flex flex-col"
          >
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h2 className="font-heading text-xl font-semibold text-foreground">
                Preview
              </h2>
              <button
                onClick={() => setShowPreview(false)}
                className="p-2 hover:bg-muted rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-muted-foreground" />
              </button>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6">
              <article className="max-w-3xl mx-auto">
                <span className="text-xs font-bold uppercase tracking-widest text-primary font-body">
                  {CATEGORIES.find(c => c.value === formData.category)?.label || 'Tech'}
                </span>
                <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground mt-4 mb-6">
                  {formData.title || 'Untitled Post'}
                </h1>
                
                {formData.featuredImage && (
                  <img
                    src={formData.featuredImage}
                    alt="Featured"
                    className="w-full h-64 object-cover rounded-lg mb-6"
                  />
                )}
                
                <div
                  className="font-body text-foreground leading-relaxed prose-custom"
                  dangerouslySetInnerHTML={{ 
                    __html: DOMPurify.sanitize(formData.content) 
                  }}
                />
              </article>
            </div>
          </motion.div>
        </div>
      )}

      {/* Custom Quill Styles */}
      <style>{`
        .blog-editor .ql-container {
          font-family: 'Manrope', sans-serif;
          font-size: 1rem;
          min-height: 400px;
        }
        .blog-editor .ql-editor {
          padding: 1.5rem;
          color: #1A1A1A;
        }
        .blog-editor .ql-editor.ql-blank::before {
          color: #6B6B6B;
          font-style: normal;
        }
        .blog-editor .ql-toolbar {
          border: none;
          border-bottom: 1px solid #D4CFC4;
          background: #F5F0E8;
        }
        .blog-editor .ql-container {
          border: none;
        }
        .blog-editor .ql-editor h1 {
          font-family: 'Cormorant Garamond', serif;
          font-size: 2rem;
          font-weight: 700;
          color: #1A1A1A;
        }
        .blog-editor .ql-editor h2 {
          font-family: 'Cormorant Garamond', serif;
          font-size: 1.5rem;
          font-weight: 600;
          color: #E07A5F;
        }
        .blog-editor .ql-editor h3 {
          font-family: 'Cormorant Garamond', serif;
          font-size: 1.25rem;
          font-weight: 600;
          color: #3D405B;
        }
        .blog-editor .ql-editor blockquote {
          border-left: 3px solid #E07A5F;
          padding-left: 1rem;
          font-style: italic;
          color: #6B6B6B;
        }
        .blog-editor .ql-editor code {
          background: #E8E4DC;
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
          font-family: 'JetBrains Mono', monospace;
        }
        .blog-editor .ql-editor pre {
          background: #1A1A1A;
          color: #E8E4DC;
          padding: 1rem;
          border-radius: 8px;
          font-family: 'JetBrains Mono', monospace;
        }
      `}</style>
    </div>
  );
}
